import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { SharedModule } from '../shared/shared.module';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { ApiService } from '@app/shared/services/api.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { UtilService } from '@app/shared/services/util.service';
import { Subscription } from 'rxjs';
import { BreadcrumbService } from '@app/shared/components/breadcrumb/breadcrumb.service';
import { certiplate_logo } from '@app/shared/constants/application-constants';

@Component({
    imports: [FormsModule, SharedModule, RouterModule],
    selector: 'app-release-notes',
    templateUrl: './releasenote.component.html',
    styleUrls: ['./releasenote.component.css'],
    changeDetection: ChangeDetectionStrategy.Default
})
export class ReleasenoteComponent implements OnInit, OnDestroy {
  releaseNotes: SafeHtml = '';
  fileKey: string = '';
  tableName: string = 'Release Notes ';
  Home: string = 'Home ';
  Release: string = 'Release History ';
  currentRoute: string;
  showBreadcrumbs: boolean = false;
  showReleaseNotesSection: boolean = false;
  isGeneratingPDF = false;
  downloadButtonText = 'Download as PDF';
  pdfGenerationError = '';
  currentVersion: string = '';
  private fromReleaseHistory: boolean = false;
  private routerSubscription: Subscription;

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
    private util: UtilService,
    private breadcrumbService: BreadcrumbService
  ) { }

  ngOnInit() {
    this.util.showLoader = true;
    
    // Get the `key` parameter from the route
    this.route.paramMap.subscribe(params => {
      this.fileKey = params.get('key') || '';
      this.fromReleaseHistory = !!this.fileKey;
      if (this.fileKey) {
        this.getReleaseNotes();
      } else {
        this.loadLatestReleaseNote();
      }
    });
    
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const currentUrl = event.urlAfterRedirects.toLowerCase();
        this.currentRoute = currentUrl;
        console.log('Current route URL:', currentUrl);

        this.showReleaseNotesSection = currentUrl.includes('releasenotes/') && decodeURIComponent(currentUrl).includes('.txt');
        console.log('Show release notes section:', this.showReleaseNotesSection);

        this.cdr.markForCheck();
      }
    });

    this.currentRoute = this.router.url;
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  async downloadAsPDF() {
    if (this.isGeneratingPDF) return;

    this.isGeneratingPDF = true;
    this.downloadButtonText = 'Preparing...';
    this.pdfGenerationError = '';
    this.cdr.detectChanges();

    try {
      await new Promise(resolve => setTimeout(resolve, 100));

      const element = document.getElementById('releaseNotesContent');
      if (!element) {
        throw new Error('Release notes content not found');
      }

      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '800px';
      document.body.appendChild(tempContainer);

      const contentClone = element.cloneNode(true) as HTMLElement;
      contentClone.style.padding = '20px';
      contentClone.style.background = 'white';
      tempContainer.appendChild(contentClone);

      await new Promise(resolve => setTimeout(resolve, 300));

      const canvas = await html2canvas(contentClone, {
        scale: 1,
        logging: true,
        useCORS: true,
        allowTaint: false,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 800,
        windowHeight: contentClone.scrollHeight
      });

      document.body.removeChild(tempContainer);

      if (!canvas || canvas.width <= 0 || canvas.height <= 0) {
        throw new Error('Failed to render content properly');
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 30;
      const footerHeight = 14;
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - headerHeight - footerHeight;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = headerHeight;
      let pageNumber = 1;
      const downloadedOn = new Date().toLocaleString();
      const totalPages = Math.max(1, Math.ceil(imgHeight / contentHeight));

      const drawReleaseBranding = (currentPage: number) => {
        try {
          const logoMaxWidth = 56;
          const logoMaxHeight = 18;
          const logoProps = pdf.getImageProperties(certiplate_logo);
          const logoRatio = logoProps.width / logoProps.height;
          let logoWidth = logoMaxWidth;
          let logoHeight = logoWidth / logoRatio;
          if (logoHeight > logoMaxHeight) {
            logoHeight = logoMaxHeight;
            logoWidth = logoHeight * logoRatio;
          }
          pdf.addImage(certiplate_logo, 'PNG', margin, 5, logoWidth, logoHeight);
        } catch (e) {
          console.error('Certiplate logo error:', e);
        }

        pdf.setDrawColor(52, 73, 94);
        pdf.setLineWidth(0.2);
        pdf.line(margin, 25, pageWidth - margin, 25);
        pdf.line(margin, pageHeight - 11, pageWidth - margin, pageHeight - 11);

        pdf.setFontSize(8);
        pdf.setTextColor(52, 73, 94);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Downloaded on: ${downloadedOn}`, margin, pageHeight - 6);
        pdf.text(`Page ${currentPage} of ${totalPages}`, pageWidth - margin, pageHeight - 6, { align: 'right' });
        pdf.text('Powered by Certiplate', pageWidth / 2, pageHeight - 6, { align: 'center' });
      };

      drawReleaseBranding(pageNumber);
      pdf.addImage(imgData, 'PNG', margin, position, contentWidth, imgHeight);

      heightLeft -= contentHeight;

      while (heightLeft > 0) {
        position -= contentHeight;
        pageNumber++;
        pdf.addPage();
        drawReleaseBranding(pageNumber);
        pdf.addImage(imgData, 'PNG', margin, position, contentWidth, imgHeight);
        heightLeft -= contentHeight;
      }

      const fileName = `Release Note_Admin Portal_${this.currentVersion}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('PDF generation error:', error);
      this.pdfGenerationError = 'Failed to generate PDF. ' + (error instanceof Error ? error.message : '');
      alert(this.pdfGenerationError);
    } finally {
      this.isGeneratingPDF = false;
      this.downloadButtonText = 'Download as PDF';
      this.cdr.detectChanges();
    }
  }

  loadLatestReleaseNote() {
    const requestBody = { key: 'certiplate-ui/' };

    this.apiService.post('/listReleaseNotes', requestBody).subscribe({
      next: (response: any) => {
        const files = response.body?.files || [];

        if (files.length > 0) {
          const sortedFiles = files.sort((a: any, b: any) => {
            return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
          });
          
          this.fileKey = sortedFiles[0].key;
          this.currentVersion = this.extractVersion(this.fileKey);
          this.updateBreadcrumbs();
          this.util.showLoader = false;
          this.getReleaseNotes();
        } else {
          console.error('No release notes found.');
          this.util.showLoader = false;
        }
      },
      error: (error) => {
        console.error('Error fetching latest release note:', error);
        this.util.showLoader = false;
      }
    });
  }

  extractVersion(fileKey: string): string {
    // Extract version from file key - supports formats like:
    // release-notes-3.1.11.7.txt
    // release-notes-1.2.3.txt
    const versionMatch = fileKey.match(/release-notes-(\d+\.\d+\.\d+(?:\.\d+)?)/);
    return versionMatch ? `v${versionMatch[1]}` : '';
  }

  home() {
    this.router.navigate(['/Dashboard'])
  }

  relasehistory() {
    this.router.navigate(['/releasehistory'])
  }

  compareVersions(versionA: string, versionB: string): number {
    const [majorA, minorA, patchA] = versionA.split('.').map(Number);
    const [majorB, minorB, patchB] = versionB.split('.').map(Number);

    if (majorA !== majorB) {
      return majorA - majorB;
    }

    if (minorA !== minorB) {
      return minorA - minorB;
    }

    return patchA - patchB;
  }

  async getReleaseNotes() {
    if (!this.fileKey) {
      console.error('No file key provided');
      return;
    }

    // Extract version and update breadcrumbs before fetching content
    this.currentVersion = this.extractVersion(this.fileKey);
    this.updateBreadcrumbs();

    const requestBody = { key: this.fileKey };

    this.apiService.post('/downloadReleaseNotes', requestBody).subscribe({
      next: async (response: any) => {
        marked.setOptions({
          gfm: true,
          breaks: true
        });

        const rawHtml = await marked(response.body.content);
        this.releaseNotes = this.sanitizer.bypassSecurityTrustHtml(rawHtml);
        this.util.showLoader = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error fetching release notes:', error);
        this.util.showLoader = false;
      }
    });
  }

  private updateBreadcrumbs() {
    const breadcrumbs = [
      {
        label: 'Home',
        url: '/Dashboard',
        disabled: false
      },
    ];

    // When navigated via a key param (from Release History), include the Release History crumb
    if (this.fromReleaseHistory) {
      breadcrumbs.push({
        label: 'Release History',
        url: '/releasehistory',
        disabled: false
      });
    }

    // Add version breadcrumb only if version is available
    if (this.currentVersion) {
      breadcrumbs.push({
        label: `${this.currentVersion} Release Notes`,
        url: this.router.url,
        disabled: true // Version breadcrumb is not clickable
      });
    }

    this.breadcrumbService.setDynamicBreadcrumb(this.router.url, breadcrumbs);
  }
}
